import { useFetchInvoiceSettings } from "@sushicorp/services";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useMemo } from "react";

import { tabs } from "./ProfileNavigation.helpers";
import Styles from "./ProfileNavigation.styles";
import { ProfileNavigationProps as Props } from "./ProfileNavigation.types";
import ActiveOrdersPreview from "components/profile/ActiveOrdersPreview/ActiveOrdersPreview";
import CONSTANTS from "config/constants";
import useAuth from "contexts/auth/auth.context.hooks";
import { useTalkShop } from "contexts/talkShop/talkShop.context.hooks";
import useI18n from "hooks/useI18n";
import useSignOut from "hooks/useSignOut";
import { notify } from "utils/common.utils";

const { WITH_TALK_SHOP, WITH_ANONYMOUS } = CONSTANTS.FEATURE_FLAGS;

const ProfileNavigation: React.FC<Props> = props => {
  const { className } = props;
  const { pathname: currentPage } = useRouter();
  const { isAnonymous } = useAuth();
  const t = useI18n();
  const signOut = useSignOut();
  const { data: invoiceSettings } = useFetchInvoiceSettings(notify);
  const { allowInvoicing } = invoiceSettings ?? {};
  const { talkShopProvider, talkShopIdentifier } = useTalkShop();

  const tabsToRender = useMemo(
    () =>
      tabs(t).filter(tab => {
        if (!tab) return false;
        const { url } = tab;
        const userIsInProfileMainPage = currentPage === "/profile";
        const isBillingPage = url === "/profile/billing";
        const isWishlistPage = url === "/profile/wishlist";
        const showBilling = !allowInvoicing && isBillingPage;

        return !(userIsInProfileMainPage && (isWishlistPage || showBilling));
      }),
    [allowInvoicing, currentPage, t]
  );

  if (!!talkShopProvider && !!talkShopIdentifier && WITH_TALK_SHOP) return null;

  return (
    <Styles className={`ProfileNavigation ${className}`}>
      {tabsToRender.map((tab, index) => {
        if (!tab) return null;
        const { icon, tabName, isExternal, url = "/", authenticated } = tab;

        if (
          tabName === `${t.profile.navigation.orderHistory}` &&
          isAnonymous &&
          !WITH_ANONYMOUS
        )
          return null;

        if (
          tabName === `${t.profile.navigation.orderHistory}` &&
          WITH_ANONYMOUS
        )
          return (
            <ActiveOrdersPreview
              className="ProfileNavigation__preview"
              key={index}
              active={currentPage.includes(url)}
            />
          );

        if (authenticated) {
          if (isExternal) {
            return (
              <a
                key={index}
                className={`ProfileNavigation__tab ${
                  currentPage.includes(url) ? "is-currently-active" : ""
                }`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {icon}
                <p
                  className={`ProfileNavigation__tab-name ${
                    currentPage.includes(url)
                      ? "ProfileNavigation__tab-name--active"
                      : ""
                  }`}
                >
                  {tabName}
                </p>
              </a>
            );
          }

          return (
            <Link key={index} href={url} passHref>
              <a
                className={`ProfileNavigation__tab ${
                  currentPage.includes(url) ? "is-currently-active" : ""
                }`}
              >
                {icon}
                <p
                  className={`ProfileNavigation__tab-name ${
                    currentPage.includes(url)
                      ? "ProfileNavigation__tab-name--active"
                      : ""
                  }`}
                >
                  {tabName}
                </p>
              </a>
            </Link>
          );
        }

        return !isAnonymous ? (
          <Link key={index} href={url} passHref>
            <a
              className={`ProfileNavigation__tab ${
                currentPage.includes(url) ? "is-currently-active" : ""
              }`}
            >
              {icon}
              <p
                className={`ProfileNavigation__tab-name ${
                  currentPage.includes(url)
                    ? "ProfileNavigation__tab-name--active"
                    : ""
                }`}
              >
                {tabName}
              </p>
            </a>
          </Link>
        ) : null;
      })}

      {!isAnonymous ? (
        <button className="ProfileNavigation__logout-button" onClick={signOut}>
          {t.profile.navigation.signOut}
        </button>
      ) : null}
    </Styles>
  );
};

ProfileNavigation.defaultProps = {
  className: ""
};

export default ProfileNavigation;
